/** Intake — log a new referral. The AI assistant turns messy text into a clean case. */
import { useState, useEffect, useCallback } from 'react';
import PhiFriendlyNote from '../../components/PhiFriendlyNote.jsx';
import { api, API, AiGlyph, Spinner, Tooltip, Hint, Badge, EmptyState, CHANNEL_LABEL, CATEGORY_LABEL, SUBJECT_TYPE_LABEL, STAGE_LABEL, dateTime } from './ui';

const BLANK = {
  source_channel: 'call_center', source_detail: '', allegation_summary: '', allegation_category: '',
  estimated_exposure: '', reporter_name: '', reporter_contact: '', reporter_anonymous: false,
  subject_type: 'provider_individual', subject_name: '', subject_npi: '',
};

export default function IntakeQueue({ onOpenCase }) {
  const [raw, setRaw] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [aiNote, setAiNote] = useState('');
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [queue, setQueue] = useState([]);
  const [loadingQueue, setLoadingQueue] = useState(true);
  const [aiSuggested, setAiSuggested] = useState(null); // what intake AI proposed (for the learning loop)
  const [attachBusy, setAttachBusy] = useState(false);
  const [attachment, setAttachment] = useState(null);   // { upload_id, filename, has_text }
  const [fileErr, setFileErr] = useState('');

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const loadQueue = useCallback(() => {
    setLoadingQueue(true);
    api('/cases?stage=intake')
      .then((j) => setQueue(j.cases || []))
      .catch(() => {})
      .finally(() => setLoadingQueue(false));
  }, []);
  useEffect(() => { loadQueue(); }, [loadQueue]);

  const runAI = async () => {
    if (!raw.trim()) return;
    setAiBusy(true); setAiNote(''); setErr('');
    try {
      const { fields } = await api('/agents/intake', { method: 'POST', body: { rawText: raw } });
      setForm((f) => ({
        ...f,
        allegation_summary: fields.allegation_summary || f.allegation_summary,
        allegation_category: fields.allegation_category || f.allegation_category,
        estimated_exposure: fields.estimated_exposure ?? f.estimated_exposure,
        reporter_name: fields.reporter_name || f.reporter_name,
        reporter_anonymous: !!fields.reporter_anonymous,
        subject_type: fields.subject_type || f.subject_type,
        subject_name: fields.subject_name || f.subject_name,
        subject_npi: fields.subject_npi || f.subject_npi,
      }));
      setAiSuggested(fields);
      setAiNote(fields.reasoning || 'Organized — please review the fields below before creating the case.');
    } catch (e) {
      setErr(e.message);
    } finally { setAiBusy(false); }
  };

  const kindToChannel = (kind, fallback) =>
    kind === 'email' ? 'email' : kind === 'medicaid_notice' ? 'medicaid' : kind === 'cms_letter' ? 'cms' : fallback;

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    setAttachBusy(true); setFileErr(''); setErr('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API}/api/sentinel/agents/intake-file`, { method: 'POST', credentials: 'include', body: fd });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json.success === false) throw new Error(json.message || 'Upload failed.');
      setAttachment({ upload_id: json.upload_id, filename: json.filename, has_text: json.has_text });
      if (json.fields) {
        const fields = json.fields;
        setForm((f) => ({
          ...f,
          allegation_summary: fields.allegation_summary || f.allegation_summary,
          allegation_category: fields.allegation_category || f.allegation_category,
          estimated_exposure: fields.estimated_exposure ?? f.estimated_exposure,
          reporter_name: fields.reporter_name || f.reporter_name,
          reporter_anonymous: !!fields.reporter_anonymous,
          subject_type: fields.subject_type || f.subject_type,
          subject_name: fields.subject_name || f.subject_name,
          subject_npi: fields.subject_npi || f.subject_npi,
          source_channel: kindToChannel(json.source_kind, f.source_channel),
        }));
        setAiSuggested(fields);
        setAiNote(fields.reasoning || 'Read the document — please review the fields below.');
      } else {
        setAiNote(json.has_text ? 'Attached and read.' : 'Attached. Could not read text automatically — please fill the fields.');
      }
    } catch (e2) { setFileErr(e2.message); } finally { setAttachBusy(false); }
  };

  const createCase = async () => {
    if (!form.allegation_summary.trim()) { setErr('Add a short description of what is alleged.'); return; }
    setSaving(true); setErr('');
    try {
      const body = {
        source_channel: form.source_channel, source_detail: form.source_detail || null,
        allegation_summary: form.allegation_summary, allegation_category: form.allegation_category || null,
        estimated_exposure: form.estimated_exposure === '' ? null : Number(form.estimated_exposure),
        reporter_name: form.reporter_anonymous ? null : (form.reporter_name || null),
        reporter_contact: form.reporter_anonymous ? null : (form.reporter_contact || null),
        reporter_anonymous: form.reporter_anonymous,
        subject: (form.subject_name || form.subject_npi)
          ? { subject_type: form.subject_type, display_name: form.subject_name, npi: form.subject_npi || null }
          : null,
        intake_upload_id: attachment?.upload_id || null,
      };
      const { case: created } = await api('/cases', { method: 'POST', body });
      // Learning loop: if the analyst changed the scheme the intake agent guessed,
      // teach it (shows in PSI ▸ Agents). Fire-and-forget; never blocks the case.
      if (aiSuggested?.allegation_category && form.allegation_category && aiSuggested.allegation_category !== form.allegation_category) {
        api(`/cases/${created.id}/agents/feedback`, { method: 'POST', body: {
          agent: 'sentinel_intake', field: 'allegation_category',
          original_value: aiSuggested.allegation_category, corrected_value: form.allegation_category,
          signal_features: { source_channel: form.source_channel },
        } }).catch(() => {});
      }
      setForm(BLANK); setRaw(''); setAiNote(''); setAiSuggested(null); setAttachment(null);
      loadQueue();
      onOpenCase(created.id);
    } catch (e) { setErr(e.message); } finally { setSaving(false); }
  };

  const input = 'w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-300';
  const labelCls = 'text-xs font-medium text-slate-600 mb-1 flex items-center';

  return (
    <div className="p-6 max-w-6xl mx-auto grid lg:grid-cols-5 gap-6">
      {/* New referral form */}
      <div className="lg:col-span-3 bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
        <div className="flex items-center">
          <h2 className="font-semibold text-slate-800">Log a new referral</h2>
          <Hint text="A referral is any tip that something might be wrong — from a member call, Medicaid, CMS, a hotline, or an internal team. Capture it here; you'll decide later whether it's a real case." />
        </div>

        {/* AI quick-start */}
        <div className="rounded-2xl bg-slate-50 border border-slate-100 p-4">
          <div className={labelCls}>
            Attach the referral document, or paste the text — the AI reads either
            <Hint text="Attach the source document (a forwarded email, a Medicaid referral notice, a CMS letter, a PDF/Word file) — or paste the raw text. The AI reads it and fills the fields below; it never invents NPIs or IDs. You review everything before saving." />
          </div>
          <textarea value={raw} onChange={(e) => setRaw(e.target.value)} rows={4}
            placeholder="e.g. Member called — says Dr. Lee's office billed for a physical they never had on 3/12. NPI 1538293847…"
            className={input + ' resize-none'} />
          <div className="mt-2 flex items-center gap-3">
            <Tooltip text="Uses your plan's private Azure OpenAI — the data stays in your tenant and is never used to train the model.">
              <button onClick={runAI} disabled={aiBusy || !raw.trim()}
                className="px-3 py-1.5 rounded-lg bg-slate-900 text-white text-sm font-medium disabled:opacity-40 hover:bg-slate-800 inline-flex items-center gap-2">
                {aiBusy ? <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <AiGlyph className="w-3.5 h-3.5" />}
                {aiBusy ? 'Organizing…' : 'Organize for me'}
              </button>
            </Tooltip>
            {aiNote && <span className="text-xs text-slate-500 italic">{aiNote}</span>}
          </div>

          <PhiFriendlyNote variant="minimum-necessary" className="mt-3" />
          <div className="mt-3 pt-3 border-t border-slate-200 flex items-center gap-3 flex-wrap">
            <label className="px-3 py-1.5 rounded-lg border border-slate-300 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 cursor-pointer inline-flex items-center gap-2">
              <input type="file" className="hidden" onChange={onFile} accept=".pdf,.docx,.doc,.txt,.eml,.msg,.rtf,.csv,.html,.htm,.png,.jpg,.jpeg,.tif,.tiff" />
              {attachBusy ? <span className="w-3.5 h-3.5 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" /> : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.6} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M18.375 12.739l-7.693 7.693a4.5 4.5 0 01-6.364-6.364l10.94-10.94A3 3 0 1119.5 7.372L8.552 18.32m.009-.01l-.01.01m5.699-9.941l-7.81 7.81a1.5 1.5 0 002.112 2.13" /></svg>
              )}
              {attachBusy ? 'Reading…' : 'Attach referral document'}
            </label>
            <Hint text="Attach the email, Medicaid referral notice, CMS letter, or PDF/Word file. It is stored as encrypted evidence on the case, and the AI reads it to fill the fields below." />
            {attachment && (
              <span className="text-xs text-slate-700 inline-flex items-center gap-1 bg-white border border-slate-200 rounded-full px-2 py-0.5">
                {attachment.filename}{!attachment.has_text && <span className="text-amber-600"> · no text found</span>}
                <button onClick={() => setAttachment(null)} className="text-slate-400 hover:text-slate-700 ml-1">✕</button>
              </span>
            )}
          </div>
          {fileErr && <p className="text-xs text-red-600 mt-1">{fileErr}</p>}
        </div>

        {/* Structured fields */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className={labelCls}>How did it arrive?<Hint text="The channel the referral came in through. Used for your reporting and to gauge source credibility." /></div>
            <select value={form.source_channel} onChange={(e) => set('source_channel', e.target.value)} className={input}>
              {Object.entries(CHANNEL_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <div className={labelCls}>Suspected scheme<Hint text="The kind of fraud/waste/abuse alleged. Don't worry if you're unsure — the AI triage step can suggest this for you." /></div>
            <select value={form.allegation_category} onChange={(e) => set('allegation_category', e.target.value)} className={input}>
              <option value="">— not sure yet —</option>
              {Object.entries(CATEGORY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
        </div>

        <div>
          <div className={labelCls}>What's alleged?<Hint text="A plain-language summary of the concern. One or two sentences is plenty." /></div>
          <textarea value={form.allegation_summary} onChange={(e) => set('allegation_summary', e.target.value)} rows={2} className={input + ' resize-none'} placeholder="Short description of the concern…" />
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div>
            <div className={labelCls}>Subject type<Hint text="Who the case is about — a provider, pharmacy, member, broker, etc." /></div>
            <select value={form.subject_type} onChange={(e) => set('subject_type', e.target.value)} className={input}>
              {Object.entries(SUBJECT_TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <div className={labelCls}>Subject name</div>
            <input value={form.subject_name} onChange={(e) => set('subject_name', e.target.value)} className={input} placeholder="e.g. Dr. Lee" />
          </div>
          <div>
            <div className={labelCls}>NPI<Hint text="The provider's National Provider Identifier, if known. Sentinel will auto-check it against your exclusion lists (if you own the FDR module)." /></div>
            <input value={form.subject_npi} onChange={(e) => set('subject_npi', e.target.value)} className={input} placeholder="10 digits" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className={labelCls}>Estimated $ at risk<Hint text="A rough dollar figure if you have one. Helps prioritize. You can refine it during the investigation." /></div>
            <input value={form.estimated_exposure} onChange={(e) => set('estimated_exposure', e.target.value)} type="number" className={input} placeholder="optional" />
          </div>
          <div>
            <div className={labelCls}>Who reported it?<Hint text="Leave blank or mark anonymous if the reporter wants protection. Anonymous identities are hidden from the case view and any reveal is logged." /></div>
            <div className="flex items-center gap-2">
              <input value={form.reporter_name} disabled={form.reporter_anonymous} onChange={(e) => set('reporter_name', e.target.value)} className={input + (form.reporter_anonymous ? ' opacity-40' : '')} placeholder="name (optional)" />
              <Tooltip text="Keep the reporter anonymous (non-retaliation). Their identity is masked everywhere and only revealed with an audited click.">
                <label className="flex items-center gap-1.5 text-xs text-slate-600 whitespace-nowrap cursor-pointer">
                  <input type="checkbox" checked={form.reporter_anonymous} onChange={(e) => set('reporter_anonymous', e.target.checked)} />
                  Anonymous
                </label>
              </Tooltip>
            </div>
          </div>
        </div>

        {err && <div className="text-sm text-red-600">{err}</div>}
        <div className="flex justify-end">
          <button onClick={createCase} disabled={saving}
            className="px-4 py-2 rounded-xl bg-red-600 text-white text-sm font-semibold disabled:opacity-50 hover:bg-red-700">
            {saving ? 'Creating…' : 'Create case →'}
          </button>
        </div>
      </div>

      {/* Intake queue */}
      <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-5">
        <div className="flex items-center mb-3">
          <h2 className="font-semibold text-slate-800">Waiting for your decision</h2>
          <Hint text="New referrals that haven't been dispositioned yet. Open one to decide: real case (investigate) or not a case (coach the referrer)." />
        </div>
        {loadingQueue ? <Spinner /> : queue.length === 0 ? (
          <EmptyState title="Nothing waiting" hint="New referrals you log will appear here until you triage them." />
        ) : (
          <div className="space-y-2">
            {queue.map((c) => (
              <button key={c.id} onClick={() => onOpenCase(c.id)}
                className="w-full text-left p-3 rounded-xl border border-slate-100 hover:border-red-200 hover:bg-red-50/40 transition-colors">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-mono text-slate-400">{c.case_number}</span>
                  <Badge tone="blue">{STAGE_LABEL[c.stage]}</Badge>
                  {c.exclusion_flag && <Badge tone="red">Excluded</Badge>}
                </div>
                <p className="text-sm text-slate-700 mt-1 line-clamp-2">{c.allegation_summary || '(no summary)'}</p>
                <p className="text-xs text-slate-400 mt-1">{CHANNEL_LABEL[c.source_channel] || c.source_channel} · {dateTime(c.created_at)}</p>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
